import DashboardLayout from "../Layouts/DashboardLayout";
import { AlertTriangle, Building2, Factory } from "lucide-react";
import { useInventory } from "../Context/InventoryContext";

export default function StockAlerts() {
  const { inventory } = useInventory();

  const lowStockAlerts = inventory
    .filter((item) => item.stock <= item.minStock)
    .sort((a, b) => a.stock - b.stock)
    .map((item) => ({
      id: item.id,
      item: item.name,
      stock: item.stock,
      min: item.minStock,
      locationType: item.branch === "Factory" ? "Factory" : "Branch",
      locationName: item.branch,
    }));

  return (
    <DashboardLayout>
      <div className="space-y-10">
        <div>
          <h1 className="text-2xl font-serif text-red-400">Stock Alerts</h1>
          <p className="text-sm text-gray-400">
            Immediate attention required for low inventory
          </p>
        </div>

        {lowStockAlerts.length === 0 ? (
          <div className="rounded-2xl bg-emerald-500/10 border border-emerald-500/30 p-8 text-center text-emerald-400">
            All stock levels are healthy
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {lowStockAlerts.map((a) => (
              <AlertCard key={a.id} alert={a} />
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function AlertCard({ alert }) {
  const isFactory = alert.locationType === "Factory";
  const critical = alert.min > 0 && alert.stock <= alert.min * 0.5;
  const priority = alert.stock === 0 ? 1 : critical ? 2 : 3;

  return (
    <div
      className={`relative rounded-2xl p-6 border
      ${
        critical
          ? "bg-red-500/15 border-red-500/40"
          : "bg-amber-400/10 border-amber-400/30"
      }`}
    >
      <div
        className={`absolute top-4 right-4
        ${critical ? "text-red-400" : "text-amber-400"}`}
      >
        <AlertTriangle size={20} />
      </div>

      <h3 className="text-lg font-medium">{alert.item}</h3>

      <div className="mt-2 flex items-center gap-2 text-sm">
        {isFactory ? (
          <Factory size={16} className="text-amber-400" />
        ) : (
          <Building2 size={16} className="text-amber-400" />
        )}
        <span className="text-gray-300">
          {alert.locationType}:{" "}
          <span className="text-white font-medium">{alert.locationName}</span>
        </span>
      </div>

      <div className="mt-4 space-y-1 text-sm text-gray-300">
        <p>
          Current Stock:{" "}
          <span className="font-medium text-white">{alert.stock || 0}</span>
        </p>
        <p>
          Minimum Required:{" "}
          <span className="font-medium text-white">{alert.min}</span>
        </p>
      </div>

      <div className="mt-4">
        <span
          className={`inline-block px-3 py-1 rounded-full text-xs
          ${
            critical
              ? "bg-red-500/20 text-red-400"
              : "bg-amber-400/20 text-amber-400"
          }`}
        >
          {alert.stock === 0
            ? "Out of Stock"
            : critical
              ? "Critical"
              : "Low Stock"}
        </span>
      </div>
    </div>
  );
}
